import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Linking,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  mdShipmentHeaders,
  mdNestedShipmentHeaders,
  shipmentHeaders,
  smShipmentHeaders,
} from "./utils/headers";
import {
  searchShipment,
  updatePage,
  updateSort,
} from "../../redux/reducers/chinaTrackerReducer";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  getChinaShipments,
  attachFileToChinaShipment,
  getChinaShipmentByContainerNo,
  deleteChinaShipment,
} from "../../redux/dispatcher/chinaTrackerDispatcher";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { chinaShipmentTranslationMap } from "../../utils/translations";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import ChinaShipmentContainerSearchModal from "./partials/ChinaShipmentContainerSearchModal";
import ChinaShipmentGenerateFormModal from "./partials/ChinaShipmentGenerateFormModal";
import { ChinaTrackerDataTableWithPagination3 } from "../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination3";
import ChinaShipmentEditFormModal from "./partials/ChinaShipmentEditFormModal";
import ChinaRecentShipmentsModal from "./partials/ChinaRecentShipmentsModal";
import { useAppTheme } from "../../hooks/useAppTheme";
import ChinaShipmentUploadModal from "./partials/ChinaShipmentUploadModal";
import ChinaShipmentMsNoMatchModal from "./partials/ChinaShipmentMsNoMatchModal";
import ChinaShipmentReplaceWarningModal from "./partials/ChinaShipmentReplaceWarningModal";
import {
  DapiConvertResponse,
  DapiFilmLogResponse,
} from "../../services/dapiService";
import { getFileFromS3 } from "../../services/s3BucketService";

export default function ChinaShipmentScreen({ navigation }: any) {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);

  const [modalOpen, setModalOpen] = useState<boolean>();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      shipmentHeaders,
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const screenWidth = Dimensions.get("window").width;
  const shouldStackUtilityCards =
    isSmallDevice || isMobileDevice || screenWidth <= 1400;
  const shouldUseCompactControls =
    isSmallDevice || isMobileDevice || screenWidth <= 1366;

  const { t } = useTranslation();
  const columnsToNotHaveThousandsSeparator: Array<string> = [
    "Packing #",
    "Order No",
    "China Item Code",
  ];

  const { language } = useSelector((state: any) => state.configurations);

  const dispatch = useDispatch<any>();
  const toast = useToast();

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const [nestedTableHeaders, setNestedTableHeaders] = useState<
    Record<string, Record<string, string | number | boolean>>
  >(translateHeaders(mdNestedShipmentHeaders, t, chinaShipmentTranslationMap));

  const dateFilterList = [
    { key: "Last 6 Months", label: t("last6Months") },
    { key: "Last Month", label: t("lastMonth") },
    { key: "Last Week", label: t("lastWeek") },
    { key: "Today", label: t("today") },
  ];
  const [selectedDateFilter, setSelectedDateFilter] = useState<string | null>(
    "Last 6 Months",
  );

  // All pagination/sort/status state comes from Redux — no local page state
  const {
    loading,
    shipments,
    totalCount,
    totalPages,
    pageSize,
    page,
    sort,
    status,
    search,
  } = useSelector((state: any) => state.chinaTracker.shipments);

  const fetchShipments = async (dateFilter?: string) => {
    let dateFrom, dateTo;

    if (dateFilter) {
      const range = getDateRange(dateFilter);
      dateFrom = range.dateFrom;
      dateTo = range.dateTo;
    }

    dispatch(
      getChinaShipments({
        page: page,
        pageSize: pageSize,
        sort: sort.direction === "asc" ? sort.key : "-" + sort.key,
        status: status,
        search: search,
        dateFrom,
        dateTo,
      }),
    );
  };
  const onClose = (shouldRefresh?: boolean) => {
    setModalOpen(false);
    if (shouldRefresh === true) {
      fetchShipments();
    }
  };

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<any>(null);

  // Open edit modal
  const handleEdit = (shipment: any) => {
    onClose();
    setSelectedShipment(shipment);
    setEditModalVisible(true);
  };

  // Removing a shipment also removes the Run assignments it made, so the tracker
  // stops counting its lines as shipped. That is not recoverable from this screen,
  // hence the confirmation — the table's trash icon fires on a single tap.
  const [shipmentToDelete, setShipmentToDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const handleDeleteRow = (id: string | number) => {
    const shipment = shipments.find((s: any) => String(s._id) === String(id));
    if (shipment) setShipmentToDelete(shipment);
  };

  const handleConfirmDelete = async () => {
    if (!shipmentToDelete || deleting) return;
    setDeleting(true);
    try {
      await dispatch(
        deleteChinaShipment(String(shipmentToDelete._id)),
      ).unwrap();
      toast.show(
        t("shipmentRemovedSuccess").replace(
          "{container}",
          String(shipmentToDelete.containerNo ?? ""),
        ),
        { type: "success" },
      );
      setShipmentToDelete(null);
      fetchShipments(selectedDateFilter || undefined);
    } catch {
      toast.show(t("shipmentRemoveFailedError"), { type: "danger" });
    } finally {
      setDeleting(false);
    }
  };

  // Opens a file filed against this container. Only the S3 object name is stored,
  // because the signed URL saveFileToS3 hands back expires within the hour — so a
  // fresh one is derived here, at the moment it is needed.
  const openShipmentAttachment = async (order: any) => {
    // The newest, because a revision under a different file name appends rather than
    // replaces it — taking the first would open the very file it superseded.
    const newest = [...(order?.attachments || [])].sort(
      (a: any, b: any) =>
        new Date(b?.uploadedAt || 0).getTime() -
        new Date(a?.uploadedAt || 0).getTime(),
    )[0];
    const fileName = newest?.fileName;
    if (!fileName) return;
    try {
      // getImageFromS3 answers with `imageUrl` whatever the file is — it signs any
      // key, the name just reflects what it was first written for.
      const result = await getFileFromS3(fileName, "china_shipments");
      const url = result?.imageUrl || result?.fileUrl || result?.signedUrl;
      if (typeof url === "string" && url) {
        await Linking.openURL(url);
      } else {
        toast.show(t("attachmentOpenFailedError").replace("{file}", fileName), {
          type: "danger",
        });
      }
    } catch {
      toast.show(t("attachmentOpenFailedError").replace("{file}", fileName), {
        type: "danger",
      });
    }
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ChinaShipmentDetailsReport", { shipment: order });
    } else if (action === "shipment_attachment") {
      void openShipmentAttachment(order);
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  useEffect(() => {
    if (isFocused) {
      fetchShipments();
    }
  }, [isFocused, sort, status, pageSize, page, search]);

  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? translateHeaders(shipmentHeaders, t, chinaShipmentTranslationMap)
        : isMediumDevice
          ? translateHeaders(mdShipmentHeaders, t, chinaShipmentTranslationMap)
          : translateHeaders(smShipmentHeaders, t, chinaShipmentTranslationMap),
    );

    setNestedTableHeaders(
      translateHeaders(mdNestedShipmentHeaders, t, chinaShipmentTranslationMap),
    );
  }, [isLargeDevice, isMediumDevice, language]);

  const handleSearch = (searchedText: string) => {
    // Reset to first page in Redux when searching
    dispatch(updatePage(1));
    if (searchedText) {
      dispatch(searchShipment(searchedText));
    } else {
      dispatch(searchShipment(""));
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      dispatch(updatePage(newPage));
    }
  };

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    dispatch(updateSort({ key, direction }));
  };

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [matchModalVisible, setMatchModalVisible] = useState(false);
  const [replaceWarningVisible, setReplaceWarningVisible] = useState(false);

  // A batch of packing lists is walked one container at a time: each is matched and
  // submitted on its own, so a failure late in the batch leaves earlier shipments
  // intact and each container's Run quantities are read fresh at its turn.
  const [batch, setBatch] = useState<DapiConvertResponse[]>([]);
  const filmLogsAwaitingShipment = useRef<DapiFilmLogResponse[]>([]);
  const [batchIdx, setBatchIdx] = useState(0);
  const [existingByContainer, setExistingByContainer] = useState<
    Record<string, any>
  >({});

  const pendingShipmentData = batch[batchIdx] ?? null;
  const existingShipmentToReplace = pendingShipmentData?.container_no
    ? (existingByContainer[
        String(pendingShipmentData.container_no).toUpperCase()
      ] ?? null)
    : null;

  const resetBatch = () => {
    setBatch([]);
    setBatchIdx(0);
    setExistingByContainer({});
  };

  const handleUploadNext = async (
    dataList: DapiConvertResponse[],
    pendingFilmLogs: DapiFilmLogResponse[] = [],
  ) => {
    // These wait for the shipments they belong to: a 装柜清单 uploaded in the same
    // batch does not exist until it is submitted, so its film log is filed once the
    // whole batch has settled.
    filmLogsAwaitingShipment.current = pendingFilmLogs;
    setUploadModalVisible(false);
    setBatch(dataList);
    setBatchIdx(0);

    // Resolve every container's existing-shipment state up front so all replace
    // confirmations are answered before any writes begin.
    const existing: Record<string, any> = {};
    for (const d of dataList) {
      if (!d.container_no) continue;
      try {
        const result = await dispatch(
          getChinaShipmentByContainerNo(d.container_no),
        ).unwrap();
        if (result?.exists) {
          existing[String(d.container_no).toUpperCase()] = result.data;
        }
      } catch {
        // Lookup failed — fall through to the normal create flow for that container
        // rather than blocking the whole batch.
      }
    }
    setExistingByContainer(existing);

    if (Object.keys(existing).length > 0) {
      setReplaceWarningVisible(true);
    } else {
      setMatchModalVisible(true);
    }
  };

  // Advances to the next container in the batch, or ends the run. Each container
  // reports success only; a failure keeps its modal open so the user can retry.
  const handleContainerSettled = () => {
    if (batchIdx + 1 < batch.length) {
      setBatchIdx(batchIdx + 1);
      return;
    }

    setMatchModalVisible(false);
    void attachPendingFilmLogs();
    resetBatch();
  };

  // Files any film log that was waiting for its shipment to exist. Best-effort: the
  // shipments are already created, so a failure here is worth telling the user about
  // but must not look like the upload failed.
  const attachPendingFilmLogs = async () => {
    const waiting = filmLogsAwaitingShipment.current;
    filmLogsAwaitingShipment.current = [];
    for (const log of waiting) {
      const container = String(log.container_no || "").trim();
      if (!container) continue;
      try {
        await dispatch(
          attachFileToChinaShipment({
            containerNo: container,
            fileName: log.file_name,
          }),
        ).unwrap();
        toast.show(t("filmLogAttachedSuccess"), { type: "success" });
      } catch {
        toast.show(
          t("filmLogAttachFailedError").replace("{file}", log.file_name),
          { type: "danger" },
        );
      }
    }
  };

  const handleConfirmReplace = () => {
    setReplaceWarningVisible(false);
    setMatchModalVisible(true);
  };

  const handleCancelReplace = () => {
    setReplaceWarningVisible(false);
    resetBatch();
  };

  const renderUtilityCards = () => {
    const alertActions: Array<{
      key: string;
      label: string;
      icon: any;
      color: string;
      textColor?: string;
      onPress?: () => void;
    }> = [
      {
        key: "generate",
        label: t("generateShipment"),
        icon: "plus",
        color: "#B8B8B8",
        onPress: () => navigateToOrder(shipments, "generate_shipment"),
      },
    ];

    alertActions.push({
      key: "recent_shipments",
      label: t("recentShipments"),
      icon: "history",
      color: "#56B7FD",
      onPress: () => navigateToOrder([], "recent_shipments"),
    });

    alertActions.push({
      key: "upload_shipments",
      label: "Upload Shipments",
      icon: "upload",
      color: "#56B7FD",
      onPress: () => setUploadModalVisible(true),
    });

    return (
      <View
        style={[
          styles.utilityContainer,
          shouldStackUtilityCards && styles.utilityContainerStacked,
        ]}
      >
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.alertsCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <View style={styles.alertActionsWrapper}>
            <View style={styles.alertActionsContainer}>
              {alertActions.map((action) => (
                <TouchableOpacity
                  key={action.key}
                  style={[
                    styles.alertActionItem,
                    !action.onPress && styles.alertActionDisabled,
                  ]}
                  onPress={action.onPress}
                  activeOpacity={action.onPress ? 0.85 : 1}
                >
                  <MaterialCommunityIcons
                    name={action.icon}
                    size={16}
                    color={action.color}
                  />
                  <Text
                    style={[
                      styles.alertActionText,
                      action.textColor && { color: action.textColor },
                    ]}
                  >
                    {action.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View
          style={[
            styles.sectionCard,
            styles.denseCard,
            styles.searchCard,
            shouldUseCompactControls && styles.sectionCardCompact,
          ]}
        >
          <LabelBannerWithSearchBar
            titleName={`${t("totalItems")}: ${totalCount ?? shipments?.length ?? 0}`}
            handleSearch={handleSearch}
            setRefresh={() => {
              dispatch(updatePage(1));
              fetchShipments(selectedDateFilter || undefined);
            }}
            dataToExport={shipments}
            fileName="ChinaShipmentReport"
            sheetName="Shipment Mains"
          />
        </View>
      </View>
    );
  };

  const getDateRange = (filter: string) => {
    const now = new Date();
    let dateFrom = new Date();

    switch (filter) {
      case "Last 6 Months":
        dateFrom.setMonth(now.getMonth() - 6);
        break;
      case "Last Month":
        dateFrom.setMonth(now.getMonth() - 1);
        break;
      case "Last Week":
        dateFrom.setDate(now.getDate() - 7);
        break;
      case "Today":
        dateFrom.setDate(now.getDate());
        break;
      default:
        return { dateFrom: null, dateTo: null };
    }

    return {
      dateFrom: dateFrom.toISOString(),
      dateTo: now.toISOString(),
    };
  };

  const renderDateFilters = () => {
    return (
      <View style={styles.quickFilterBar}>
        <Text style={styles.quickFilterLabel}>{t("dateFilter")}</Text>

        <View style={styles.filterChipsWrap}>
          {dateFilterList.map((option, index) => {
            const isActive = selectedDateFilter === option.key;

            return (
              <TouchableOpacity
                key={index}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => {
                  const newFilter =
                    selectedDateFilter === option.key ? null : option.key;

                  setSelectedDateFilter(newFilter);

                  dispatch(updatePage(1));

                  fetchShipments(newFilter || undefined);
                }}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };
  return (
    <View style={styles.container}>
      <View style={styles.topContent}>
        {renderDateFilters()}
        {renderUtilityCards()}
      </View>

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: themeColors.surface, flex: 1 }}>
            <ChinaTrackerDataTableWithPagination3
              isToExpandAllRows={false}
              isRowsExpandable={true}
              headers={tableHeaders}
              isNestedDataLoaded={loading}
              rows={shipments}
              key="rowKeyValue"
              rowKeyValue="_id"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              itemsPerPage={pageSize}
              handleSortColumn={handleSort}
              operationActions={navigateToOrder}
              itemTracker={true}
              nestedHeader={nestedTableHeaders}
              nestedItemKeyValue="items"
              // Show every line a container holds, including the one-off and film
              // lines that carry no order number — paging these at the outer page
              // size hid them entirely.
              nestedItemsPerPage={500}
              useModal={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              isDelete={true}
              handleDeleteRow={handleDeleteRow}
              t={t}
              page={page - 1}
              setPage={handlePageChange}
            />
          </View>
        )}
      </ScrollView>

      {modalOpen && selectedEnquiryType === "generate_shipment" && (
        <ChinaShipmentContainerSearchModal
          onClose={onClose}
          visible={modalOpen}
          setSelectedEnquiryType={setSelectedEnquiryType}
        />
      )}

      {modalOpen && selectedEnquiryType === "generate_shipment_form" && (
        <ChinaShipmentGenerateFormModal onClose={onClose} visible={modalOpen} />
      )}

      {/* Shipment Edit Before Approval */}
      <ChinaShipmentEditFormModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        shipment={selectedShipment}
      />

      {modalOpen && selectedEnquiryType === "recent_shipments" && (
        <ChinaRecentShipmentsModal
          onClose={() => onClose()}
          visible={modalOpen}
        />
      )}

      <ChinaShipmentUploadModal
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
        onNext={handleUploadNext}
      />

      <ChinaShipmentReplaceWarningModal
        visible={replaceWarningVisible}
        containerNo={Object.values(existingByContainer)
          .map((sh: any) => sh?.containerNo)
          .filter(Boolean)
          .join(", ")}
        onCancel={handleCancelReplace}
        onConfirm={handleConfirmReplace}
      />

      <ChinaShipmentReplaceWarningModal
        visible={!!shipmentToDelete}
        containerNo={String(shipmentToDelete?.containerNo ?? "")}
        titleKey="removeShipmentWarningTitle"
        messageKey="removeShipmentWarningMessage"
        confirmKey="removeShipmentBtn"
        confirmDisabled={deleting}
        onCancel={() => setShipmentToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <ChinaShipmentMsNoMatchModal
        visible={matchModalVisible}
        data={pendingShipmentData}
        existingShipment={existingShipmentToReplace}
        batchPosition={
          batch.length > 1
            ? { index: batchIdx + 1, total: batch.length }
            : undefined
        }
        onSubmitted={handleContainerSettled}
        onClose={() => {
          setMatchModalVisible(false);
          resetBatch();
        }}
      />
    </View>
  );
}

function createStyles(colors: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.surface,
    },
    headerContainer: {
      paddingHorizontal: 20,
      paddingTop: 16,
      paddingBottom: 8,
      backgroundColor: colors.surface,
    },
    topContent: {
      gap: 12,
      marginBottom: 10,
    },
    sectionCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 12,
      shadowColor: "rgba(15, 23, 42, 0.08)",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 1,
      shadowRadius: 12,
      elevation: 2,
    },
    denseCard: {
      paddingVertical: 10,
      paddingHorizontal: 10,
    },
    sectionCardCompact: {
      padding: 8,
      borderRadius: 10,
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: "bold",
      color: colors.text,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 6,
    },
    sectionTitleCompact: {
      fontSize: 12,
      marginBottom: 4,
    },
    filterChipRow: {
      paddingRight: 8,
      paddingBottom: 2,
    },
    filterChipRowCompact: {
      paddingBottom: 0,
    },
    filterCard: {
      flexDirection: "row",
      gap: 12,
    },
    filterCardStacked: {
      flexDirection: "column",
    },
    cardHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    headerLabelGroup: {
      flex: 1,
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 12,
    },
    filterColumnLabel: {
      flex: 1,
    },
    filterColumn: {
      flex: 1,
    },
    utilityContainer: {
      flexDirection: "row",
      gap: 16,
    },
    utilityContainerStacked: {
      flexDirection: "column",
    },
    alertsCard: {
      flex: 1,
    },
    alertActionsWrapper: {
      minHeight: 64,
      justifyContent: "center",
    },
    alertActionsContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
      justifyContent: "flex-start",
      alignItems: "center",
    },
    alertActionItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 14,
      borderRadius: 999,
      backgroundColor: colors.surfaceSubtle,
    },
    alertActionDisabled: {
      opacity: 0.5,
    },
    alertActionText: {
      fontSize: 12,
      fontWeight: "600",
      color: colors.text,
    },
    searchCard: {
      flex: 1,
      justifyContent: "center",
    },
    contentContainer: {
      flex: 1,
    },
    spinnerContainer: {
      position: "absolute",
      top: 100,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    },

    quickFilterBar: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      paddingVertical: 16,
      paddingHorizontal: 10,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    quickFilterChipsContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingRight: 12,
    },
    quickFilterLabel: {
      fontWeight: "700",
      fontSize: 13,
      color: colors.textSecondary,
      marginRight: 10,
    },
    filterChip: {
      paddingVertical: 6,
      paddingHorizontal: 14,
      backgroundColor: colors.surfaceSubtle,
      borderRadius: 999,
      marginRight: 8,
      marginBottom: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    filterChipActive: {
      backgroundColor: "#61B8FF",
      borderColor: "#61B8FF",
    },
    filterChipText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    filterChipTextActive: {
      color: "#fff",
      fontSize: 13,
      fontWeight: "600",
    },

    filterChipsWrap: {
      flexDirection: "row",
      flexWrap: "wrap",
      marginTop: 8,
    },
  });
}
